import React, { useState, useEffect } from 'react';
import api from '../config/axios';
import { useAuth } from '../context/AuthContext';
import { FaSync, FaFilter, FaDownload, FaCalendarAlt, FaCode } from 'react-icons/fa';
import './WinBOOrders.css';

interface WinBOOrder {
  fechaSolicitud: string;
  estado: string;
  cliente: string;
  tipoTrabajo: string;
  cuadrilla: string;
  movil: string;
  numeroDocumento: string;
  codSeguimiento: string;
  motivoCancelacion: string;
  producto?: string;
}

interface WinBOOrdersResponse {
  orders: WinBOOrder[];
  total: number;
  filters: any;
  rawHtml?: string;
  htmlLength?: number;
  syncResults?: {
    creadas: number;
    actualizadas: number;
    errores: number;
    saltadas?: number;
    saltadasDetalle?: Array<{
      cliente: string;
      identificador: string;
      razon: string;
    }>;
    mensaje?: string;
    mensajesEnviados?: number;
    procesando?: boolean;
  };
}

// Funciones auxiliares fuera del componente
const formatDate = (date: Date): string => {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const formatDateForInput = (date: Date): string => {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${year}-${month}-${day}`;
};

const parseDateFromInput = (dateString: string): string => {
  if (!dateString) return '';
  const [year, month, day] = dateString.split('-');
  return `${day}/${month}/${year}`;
};

const getDefaultDates = () => {
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1); // Un día atrás
  return {
    fechaEstaDesde: formatDateForInput(yesterday),
    fechaEstaHasta: formatDateForInput(today)
  };
};

const WinBOOrders: React.FC = () => {
  const { user } = useAuth();
  const [orders, setOrders] = useState<WinBOOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState({
    ...getDefaultDates(),
    estado: '0',
    OrdenId: '',
    NumeDocu: '',
    Nombre: ''
  });
  const [lastSync, setLastSync] = useState<Date | null>(null);
  const [showHtmlModal, setShowHtmlModal] = useState(false);
  const [rawHtml, setRawHtml] = useState<string>('');

  // Cargar órdenes automáticamente al montar el componente
  useEffect(() => {
    loadOrders();
  }, []);

  // Cargar órdenes desde la base de datos
  const loadOrders = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Validar rango de fechas (máximo 7 días)
      if (filters.fechaEstaDesde && filters.fechaEstaHasta) {
        const fechaDesde = new Date(filters.fechaEstaDesde);
        const fechaHasta = new Date(filters.fechaEstaHasta);
        const diffTime = Math.abs(fechaHasta.getTime() - fechaDesde.getTime());
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        if (diffDays > 7) {
          setError(`El rango de fechas no puede exceder 7 días. Rango seleccionado: ${diffDays} días.`);
          setLoading(false);
          return;
        }
      }
      
      // Preparar query params con las fechas del formulario
      const params = new URLSearchParams();
      
      if (filters.fechaEstaDesde) {
        // Convertir de YYYY-MM-DD a DD/MM/YYYY
        const [year, month, day] = filters.fechaEstaDesde.split('-');
        params.append('fechaDesde', `${day}/${month}/${year}`);
      }
      
      if (filters.fechaEstaHasta) {
        // Convertir de YYYY-MM-DD a DD/MM/YYYY
        const [year, month, day] = filters.fechaEstaHasta.split('-');
        params.append('fechaHasta', `${day}/${month}/${year}`);
      }
      
      if (filters.estado && filters.estado !== '0') {
        params.append('estado', filters.estado);
      }
      
      if (filters.OrdenId) {
        params.append('numeroOrden', filters.OrdenId);
      }
      
      if (filters.NumeDocu) {
        params.append('documento', filters.NumeDocu);
      }
      
      const response = await api.get(`/winbo/orders?${params.toString()}`);
      setOrders(response.data.orders || []);
      setLastSync(new Date());
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error al cargar órdenes de WinBO');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Sincronizar órdenes desde WinBO a la base de datos
  const handleSync = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Preparar filtros con las fechas del formulario
      const today = new Date();
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      
      const fechaDesdeStr = filters.fechaEstaDesde 
        ? filters.fechaEstaDesde
        : formatDateForInput(yesterday);
      const fechaHastaStr = filters.fechaEstaHasta 
        ? filters.fechaEstaHasta
        : formatDateForInput(today);
      
      // Validar rango de fechas (máximo 7 días)
      const fechaDesdeDate = new Date(fechaDesdeStr);
      const fechaHastaDate = new Date(fechaHastaStr);
      const diffTime = Math.abs(fechaHastaDate.getTime() - fechaDesdeDate.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays > 7) {
        setError(`El rango de fechas no puede exceder 7 días. Rango seleccionado: ${diffDays} días.`);
        setLoading(false);
        return;
      }
      
      const fechaDesde = parseDateFromInput(fechaDesdeStr);
      const fechaHasta = parseDateFromInput(fechaHastaStr);
      
      const requestFilters = {
        fechaEstaDesde: fechaDesde,
        fechaEstaHasta: fechaHasta,
        estado: filters.estado || '0',
        OrdenId: filters.OrdenId || '',
        NumeDocu: filters.NumeDocu || '',
        Nombre: filters.Nombre || '',
        tipoOrden: 1,
        pagiActu: 1,
        idPage: 74,
        sync: true // Enviar a SQS para sincronización
      };
      
      const response = await api.post<WinBOOrdersResponse>('/winbo/orders', requestFilters);
      
      // Mostrar mensaje de confirmación
      if (response.data.syncResults) {
        const { mensaje, mensajesEnviados } = response.data.syncResults;
        alert(mensaje || `${mensajesEnviados || 0} páginas enviadas a sincronización. Los datos estarán disponibles en unos momentos.`);
        
        // Recargar órdenes después de un breve delay
        setTimeout(() => {
          loadOrders();
        }, 2000);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error al sincronizar órdenes');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date: Date): string => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };


  const parseDateFromInput = (dateString: string): string => {
    if (!dateString) return '';
    const [year, month, day] = dateString.split('-');
    return `${day}/${month}/${year}`;
  };

  const handleFilterChange = (field: string, value: string) => {
    // Actualizar filtros sin validar inmediatamente
    // La validación se hará solo al cargar o sincronizar
    const newFilters = { ...filters, [field]: value };
    setFilters(newFilters);
    
    // Si se está cambiando una fecha y hay un error previo, limpiarlo
    // para permitir que el usuario seleccione libremente
    if ((field === 'fechaEstaDesde' || field === 'fechaEstaHasta') && error) {
      // Solo limpiar el error si el rango es válido cuando ambas fechas están presentes
      if (newFilters.fechaEstaDesde && newFilters.fechaEstaHasta) {
        const fechaDesde = new Date(newFilters.fechaEstaDesde);
        const fechaHasta = new Date(newFilters.fechaEstaHasta);
        const diffTime = Math.abs(fechaHasta.getTime() - fechaDesde.getTime());
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        if (diffDays <= 7) {
          setError(null);
        }
      } else {
        // Si solo hay una fecha seleccionada, limpiar el error para permitir selección
        setError(null);
      }
    }
  };

  const handleReset = () => {
    setFilters({
      ...getDefaultDates(),
      estado: '0',
      OrdenId: '',
      NumeDocu: '',
      Nombre: ''
    });
  };

  const getEstadoBadgeClass = (estado: string) => {
    const estadoLower = estado.toLowerCase();
    if (estadoLower.includes('agendada') || estadoLower.includes('programada')) {
      return 'bg-info';
    }
    if (estadoLower.includes('completada') || estadoLower.includes('finalizada')) {
      return 'bg-success';
    }
    if (estadoLower.includes('cancelada') || estadoLower.includes('rechazada')) {
      return 'bg-danger';
    }
    if (estadoLower.includes('en proceso') || estadoLower.includes('en curso')) {
      return 'bg-warning text-dark';
    }
    return 'bg-secondary';
  };

  if (user?.role !== 'administrador' && user?.role !== 'supervisor') {
    return (
      <div className="container-fluid py-4">
        <div className="alert alert-warning" role="alert">
          No tienes permisos para acceder a esta sección.
        </div>
      </div>
    );
  }

  return (
    <div className="winbo-orders-container">
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4 gap-3">
        <div>
          <h2 className="mb-0">Órdenes de WinBO</h2>
          <p className="text-muted mb-0 small">
            Sincronización de órdenes de trabajo desde WinBO
            {lastSync && (
              <span className="ms-2">
                Última sincronización: {lastSync.toLocaleTimeString('es-ES')}
              </span>
            )}
          </p>
        </div>
        <div className="d-flex gap-2">
          {rawHtml && (
            <button
              className="btn btn-outline-info d-flex align-items-center gap-2"
              onClick={() => setShowHtmlModal(true)}
              title="Ver HTML recibido de WinBO"
            >
              <FaCode /> Ver HTML
            </button>
          )}
          <button
            className="btn btn-primary d-flex align-items-center gap-2"
            onClick={handleSync}
            disabled={loading}
            style={{ backgroundColor: '#fe5f0b', borderColor: '#fe5f0b' }}
          >
            <FaSync className={loading ? 'fa-spin' : ''} />
            {loading ? 'Sincronizando...' : 'Sincronizar'}
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button
            type="button"
            className="btn-close"
            onClick={() => setError(null)}
            aria-label="Close"
          ></button>
        </div>
      )}

      {/* Filtros */}
      <div className="card shadow-sm mb-4">
        <div className="card-header bg-light">
          <h5 className="mb-0 d-flex align-items-center gap-2">
            <FaFilter /> Filtros de Búsqueda
          </h5>
        </div>
        <div className="card-body">
          <div className="row g-3">
            <div className="col-12 col-md-8 col-lg-5">
              <label className="form-label small">
                <FaCalendarAlt className="me-1" />
                Rango de Fechas
              </label>
              <div className="d-flex align-items-center gap-2">
                <div className="flex-grow-1">
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={filters.fechaEstaDesde}
                    onChange={(e) => handleFilterChange('fechaEstaDesde', e.target.value)}
                    max={filters.fechaEstaHasta || undefined}
                    title="Fecha de inicio"
                  />
                </div>
                <span className="text-muted small fw-bold">→</span>
                <div className="flex-grow-1">
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={filters.fechaEstaHasta}
                    onChange={(e) => handleFilterChange('fechaEstaHasta', e.target.value)}
                    min={filters.fechaEstaDesde || undefined}
                    title="Fecha de fin"
                  />
                </div>
              </div>
              <small className="text-muted d-block mt-1">
                <span className="badge bg-secondary">Máximo 7 días</span>
              </small>
            </div>
            <div className="col-12 col-md-6 col-lg-2">
              <label className="form-label small">Estado</label>
              <select
                className="form-select form-select-sm"
                value={filters.estado}
                onChange={(e) => handleFilterChange('estado', e.target.value)}
              >
                <option value="0">Todos</option>
                <option value="1">Agendada</option>
                <option value="2">En Proceso</option>
                <option value="3">Completada</option>
                <option value="4">Cancelada</option>
              </select>
            </div>
            <div className="col-12 col-md-6 col-lg-2">
              <label className="form-label small">N° Orden</label>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Buscar..."
                value={filters.OrdenId}
                onChange={(e) => handleFilterChange('OrdenId', e.target.value)}
              />
            </div>
            <div className="col-12 col-md-6 col-lg-2">
              <label className="form-label small">Documento</label>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Buscar..."
                value={filters.NumeDocu}
                onChange={(e) => handleFilterChange('NumeDocu', e.target.value)}
              />
            </div>
            <div className="col-12 d-flex gap-2">
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => {
                  handleReset();
                  loadOrders();
                }}
              >
                Limpiar
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabla de órdenes */}
      <div className="card shadow-sm">
        <div className="card-body">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Cargando...</span>
              </div>
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <p>No se encontraron órdenes</p>
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="table table-hover align-middle">
                  <thead className="table-dark">
                    <tr>
                      <th>Fecha Solicitud</th>
                      <th>Estado</th>
                      <th>Cliente</th>
                      <th>Tipo Trabajo</th>
                      <th className="d-none d-md-table-cell">Cuadrilla</th>
                      <th className="d-none d-lg-table-cell">Móvil</th>
                      <th className="d-none d-lg-table-cell">Documento</th>
                      <th className="d-none d-xl-table-cell">Cód. Seguimiento</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((order, index) => (
                      <tr key={index}>
                        <td className="small">{order.fechaSolicitud}</td>
                        <td>
                          <span className={`badge ${getEstadoBadgeClass(order.estado)}`}>
                            {order.estado}
                          </span>
                        </td>
                        <td>
                          <div className="text-truncate" style={{ maxWidth: '200px' }}>
                            {order.cliente}
                          </div>
                        </td>
                        <td className="small">{order.tipoTrabajo}</td>
                        <td className="d-none d-md-table-cell small">{order.cuadrilla}</td>
                        <td className="d-none d-lg-table-cell">{order.movil}</td>
                        <td className="d-none d-lg-table-cell small">{order.numeroDocumento}</td>
                        <td className="d-none d-xl-table-cell small">{order.codSeguimiento}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-3 d-flex justify-content-between align-items-center">
                <div className="text-muted small">
                  Mostrando {orders.length} órdenes
                  {rawHtml && (
                    <span className="ms-2">
                      (HTML: {rawHtml.length} caracteres)
                    </span>
                  )}
                </div>
                {rawHtml && (
                  <button
                    className="btn btn-sm btn-outline-info"
                    onClick={() => setShowHtmlModal(true)}
                    title="Ver HTML recibido"
                  >
                    <FaCode className="me-1" />
                    Ver HTML
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Modal para ver HTML */}
      {showHtmlModal && (
        <div className="modal show" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: 1050 }}>
          <div className="modal-dialog modal-dialog-centered modal-xl" style={{ maxWidth: '90%', margin: 'auto' }}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">HTML Recibido de WinBO</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowHtmlModal(false)}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body" style={{ maxHeight: '70vh', overflow: 'auto' }}>
                <div className="mb-3">
                  <strong>Total de caracteres:</strong> {rawHtml.length}
                </div>
                <div className="mb-3">
                  <strong>Vista previa (primeros 500 caracteres):</strong>
                  <pre style={{ 
                    backgroundColor: '#f8f9fa', 
                    padding: '1rem', 
                    borderRadius: '0.25rem',
                    fontSize: '0.85rem',
                    maxHeight: '200px',
                    overflow: 'auto',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word'
                  }}>
                    {rawHtml.substring(0, 500)}...
                  </pre>
                </div>
                <div>
                  <strong>HTML Completo:</strong>
                  <pre style={{ 
                    backgroundColor: '#f8f9fa', 
                    padding: '1rem', 
                    borderRadius: '0.25rem',
                    fontSize: '0.75rem',
                    maxHeight: '400px',
                    overflow: 'auto',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word'
                  }}>
                    {rawHtml}
                  </pre>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowHtmlModal(false)}
                >
                  Cerrar
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    navigator.clipboard.writeText(rawHtml);
                    alert('HTML copiado al portapapeles');
                  }}
                  style={{ backgroundColor: '#fe5f0b', borderColor: '#fe5f0b' }}
                >
                  Copiar HTML
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WinBOOrders;

